import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import {
  SchemaNewPassword,
  SchemaNewPasswordType,
} from "@pages/reset-password/schema/recover-password";

import { useAlterarSenhaUsuario } from "@/hooks/alterar-senha-usuario";

import Logo from "@/common/assets/png/logo-let-scom.png";

const ResetPasswordConfirm = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [validToken, setValidToken] = useState(true);

  const { alterarSenha, isLoading } = useAlterarSenhaUsuario();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SchemaNewPasswordType>({
    resolver: zodResolver(SchemaNewPassword),
  });

  useEffect(() => {
    if (!token) setValidToken(false);
  }, [token]);

  const onSubmit = (data: SchemaNewPasswordType) => {
    if (!token) return;

    alterarSenha(
      {
        token: token,
        senha: data.password,
        novaSenha: data.password,
      },
      {
        onSuccess: (data) => {
          if (data?.code === 200) {
            navigate("/");
          }
          return;
        },
      }
    );
  };

  if (!validToken) {
    return <p className="mt-10 text-center text-red-600">Token inválido ou ausente.</p>;
  }

  return (
    <div className="flex h-screen flex-col lg:flex-row">
      {/* Área de Recuperação */}
      <div className="flex h-full w-full flex-col items-center justify-center bg-gray-100 p-8 lg:w-1/2">
        <img src={Logo} alt="logo" className="mb-8 h-[150px] w-[150px]" />
        <div className="w-full lg:w-1/2">
          <h1 className="mb-2 text-2xl font-bold text-neutral900">Redefinir senha</h1>
          <p className="mb-6 font-sans text-neutral900">Informe uma senha nova.</p>
        </div>

        <form className="w-full lg:w-1/2" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label className="mb-2 block text-lg font-normal text-neutral900">Nova senha:</label>
            <input
              {...register("password")}
              type="password"
              placeholder="Digite a nova senha"
              className="focus:shadow-outline mb-3 w-full appearance-none rounded border px-3 py-2 leading-tight text-gray-700 shadow focus:outline-none"
            />
            {errors.password && <p className="text-sm text-red-500">{errors.password.message}</p>}
          </div>

          <div>
            <label className="mb-2 block text-lg font-normal text-neutral900">
              Confirmar senha:
            </label>
            <input
              {...register("confirmPassword")}
              type="password"
              placeholder="Confirme a nova senha"
              className="focus:shadow-outline mb-3 w-full appearance-none rounded border px-3 py-2 leading-tight text-gray-700 shadow focus:outline-none"
            />
            {errors.confirmPassword && (
              <p className="text-sm text-red-500">{errors.confirmPassword.message}</p>
            )}
          </div>

          <div className="mt-4 flex w-full flex-col items-center justify-center">
            <button
              disabled={isLoading}
              className="focus:shadow-outline mb-2 w-full rounded bg-neutral900 px-4 py-2 font-bold text-white focus:outline-none disabled:opacity-50"
              type="submit"
            >
              {isLoading ? "Enviando..." : "Enviar"}
            </button>

            <button
              onClick={() => navigate("/")}
              className="inline-block align-baseline text-sm font-medium text-blue-500 hover:text-blue-800"
            >
              Voltar
            </button>
          </div>
        </form>

        <p className="mt-8 text-xs text-gray-500">Let´scom © 2026 - Todos os direitos reservados</p>
      </div>

      {/* Área de Imagem */}
      <div className="hidden h-full lg:block lg:w-1/2">
        <img
          src="https://media.istockphoto.com/id/1496375910/pt/foto/check-in-using-smartphone-qr-code.jpg?s=2048x2048&w=is&k=20&c=PR20UxjxIamferUw1ZgGfw1hao1APP9WQNLPaOQfEC4="
          alt="background"
          className="h-full w-full object-cover"
        />
      </div>
    </div>
  );
};

/**
 * EXPORTS
 */
export { ResetPasswordConfirm };
